import { motion, AnimatePresence } from 'framer-motion'
import * as React from 'react'

import { DraggableCarousel } from '#app/components/ui/draggable-carousel.tsx'
import { InsuranceCard } from '#app/components/ui/insurance-card.tsx'
import { Tab, TabGroup } from '#app/components/ui/tab.tsx'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './ui'
import { Tag } from './ui/tag'

export interface InsuranceType {
	/**
	 * Insurance type identifier
	 */
	id: string
	/**
	 * Display title
	 */
	title: string
	/**
	 * Icon name
	 */
	iconName: string
	/**
	 * Card color variant
	 */
	color?: 'blue' | 'green'
	/**
	 * Optional link URL
	 */
	link?: string
}

export interface InsuranceSectionProps {
	/**
	 * Section title
	 */
	title?: string
	/**
	 * Section subtitle
	 */
	subtitle?: string
	/**
	 * Tag/chip text
	 */
	tagText?: string
	/**
	 * Available tabs
	 */
	tabs: Array<{
		id: string
		label: string
		iconName: string
		insurances: InsuranceType[]
	}>
	/**
	 * Currently selected tab
	 */
	selectedTab?: string
	/**
	 * Tab change handler
	 */
	onTabChange?: (tabId: string) => void
	/**
	 * Insurance card click handler
	 */
	onInsuranceClick?: (insuranceLink: string | undefined) => void
	/**
	 * Additional CSS classes
	 */
	className?: string
}

const InsuranceSection = React.forwardRef<HTMLElement, InsuranceSectionProps>(
	(
		{
			title = 'Cotizá con propósito',
			subtitle = 'Tu elección puede hacer una diferencia real.',
			tagText = 'Seguros a tu medida',
			tabs,
			selectedTab,
			onTabChange,
			onInsuranceClick,
			className,
			...props
		},
		ref,
	) => {
		const [activeTab, setActiveTab] = React.useState(
			selectedTab || tabs[0]?.id || '',
		)

		const handleTabChange = (tabId: string) => {
			setActiveTab(tabId)
			onTabChange?.(tabId)
		}

		const currentTab = tabs.find((tab) => tab.id === activeTab) || tabs[0]

		return (
			<section ref={ref} className={cn('bg-white py-8', className)} {...props}>
				<div className="mx-auto px-6">
					<div className="flex w-full flex-col items-center justify-start gap-6">
						{/* Tag/Chip */}
						{tagText && (
							<Tag
								size="l"
								corner="rounded"
								variant="secondary"
								color="green"
								appearance="solid"
								leftIcon="service"
								option="text-icon-left"
							>
								{tagText}
							</Tag>
						)}

						{/* Main Content Container */}
						<div className="relative flex w-full shrink-0 flex-col items-start justify-start gap-8">
							{/* Header Section */}
							<div className="relative flex w-full shrink-0 flex-col items-start justify-center gap-8">
								{/* Title and Subtitle */}
								<div className="relative flex w-full shrink-0 flex-col items-center justify-start gap-4">
									{/* Title - Responsive sizing */}
									<div className="relative flex w-full max-w-[1200px] shrink-0 items-center justify-center gap-6">
										<div className="relative flex min-h-px w-full min-w-px shrink-0 flex-col items-start justify-start gap-1">
											<div className="font-parkinsans relative w-full shrink-0 text-center text-[32px] leading-[45px] font-medium text-blue-800 not-italic md:text-[56px] md:leading-[70px]">
												<p>
													<span>{title.split(' ')[0]} </span>
													<span className="text-green-500">
														{title.split(' ').slice(1).join(' ')}
													</span>
												</p>
											</div>
										</div>
									</div>

									{/* Subtitle - Mobile responsive width */}
									<div className="relative flex w-full max-w-[1200px] shrink-0 items-center justify-center gap-6">
										<div className="relative flex min-h-px w-full max-w-[278px] min-w-px shrink-0 flex-col items-start justify-start gap-1 md:max-w-full">
											<div className="font-inter relative w-full shrink-0 text-center text-[16px] leading-[26px] font-normal text-neutral-600 not-italic md:text-[18px] md:leading-[36px]">
												<p>{subtitle}</p>
											</div>
										</div>
									</div>
								</div>

								{/* Tabs Navigation */}
								<div className="relative flex w-full shrink-0 items-center justify-center">
									<TabGroup
										selectedTab={activeTab}
										onTabChange={handleTabChange}
										orientation="horizontal"
										alignment="center"
										size="default"
									>
										{tabs.map((tab) => (
											<Tab
												className="cursor-pointer"
												key={tab.id}
												id={tab.id}
												icon={tab.iconName as any}
												variant={
													tab.id === 'empresas' ? 'secondary' : 'primary'
												}
											>
												{tab.label}
											</Tab>
										))}
									</TabGroup>
								</div>
							</div>

							{/* Insurance Cards Grid */}
							<div className="relative flex w-full flex-col items-center justify-center gap-4">
								<AnimatePresence mode="wait">
									<motion.div
										key={activeTab}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -20 }}
										transition={{
											duration: 0.1,
											ease: [0.25, 0.46, 0.45, 0.94],
										}}
										className="w-full"
									>
										<DraggableCarousel
											gap={16}
											padding={20}
											momentumFactor={0.15}
											elasticFactor={0.1}
											springConfig={{
												damping: 35,
												stiffness: 150,
												mass: 1.5,
											}}
										>
											{currentTab?.insurances.map((insurance, index) => (
												<motion.div
													key={insurance.id}
													initial={{ opacity: 0, scale: 0.9, y: 20 }}
													animate={{ opacity: 1, scale: 1, y: 0 }}
													transition={{
														duration: 0.3,
														delay: index * 0.05,
														ease: [0.25, 0.46, 0.45, 0.94],
													}}
												>
													<InsuranceCard
														title={insurance.title}
														iconName={insurance.iconName}
														color={insurance.color}
														onClick={() => onInsuranceClick?.(insurance.link)}
													/>
												</motion.div>
											))}
										</DraggableCarousel>
									</motion.div>
								</AnimatePresence>
							</div>
							<div className="relative my-15 flex w-full items-center justify-center">
								<Button size="l">Conocé todas las coberturas</Button>
							</div>
						</div>
					</div>
				</div>
			</section>
		)
	},
)

InsuranceSection.displayName = 'InsuranceSection'

export { InsuranceSection }
